// Realtime notification listener for the authenticated user.
//
// Subscribes to a per-user broadcast channel — `user-events:{user_id}` — and
// renders Sonner toasts when the server pushes invite or message events. The
// channel is public (no `private: true`) for now; payloads are intentionally
// minimal (ids + display names) so a hypothetical eavesdropper learns nothing
// confidential. Migrate to private channels with RLS on realtime.messages
// when we want to include message snippets directly in the broadcast.
//
// Mounted once in <AppContent /> while a user is authed. Re-subscribes on
// user.id change and tears down on logout.
//
// The toast handler does NOT ack anything — email delivery is independent
// (see send-stale-thread-reminders edge function).

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

type InviteReceivedPayload = {
  kind: 'invite_received';
  property_address: string | null;
  role: string;
  sender_name: string | null;
};

type MessageReceivedPayload = {
  kind: 'message_received';
  thread_id: string;
  property_id: string | null;
  sender_name: string | null;
};

type AnalysisCompletedPayload = {
  kind: 'analysis_completed';
  property_id: string;
  property_address: string | null;
};

type AnalysisRerunningPayload = {
  kind: 'analysis_rerunning';
  property_id: string;
  property_address: string | null;
  uploader_name: string | null;
};

type NotifyPayload =
  | InviteReceivedPayload
  | MessageReceivedPayload
  | AnalysisCompletedPayload
  | AnalysisRerunningPayload;

function isNotifyPayload(p: unknown): p is NotifyPayload {
  if (!p || typeof p !== 'object') return false;
  const k = (p as { kind?: unknown }).kind;
  return (
    k === 'invite_received' ||
    k === 'message_received' ||
    k === 'analysis_completed' ||
    k === 'analysis_rerunning'
  );
}

export function userEventsChannelName(userId: string): string {
  return `user-events:${userId}`;
}

export function useNotificationsChannel(userId: string | null | undefined): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    const channelName = userEventsChannelName(userId);
    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'notify' }, (msg) => {
        const payload = (msg as { payload?: unknown }).payload;
        if (!isNotifyPayload(payload)) {
          console.warn('[useNotificationsChannel] dropping malformed payload', payload);
          return;
        }
        if (payload.kind === 'invite_received') {
          const who = payload.sender_name?.trim() || 'Someone';
          const where = payload.property_address?.trim() || 'a property';
          toast.message(`${who} invited you to view ${where}`, {
            description: `Role: ${humanizeRole(payload.role)}`,
            action: {
              label: 'Open',
              onClick: () => navigate('/listings'),
            },
          });
          return;
        }
        if (payload.kind === 'message_received') {
          const who = payload.sender_name?.trim() || 'Someone';
          toast.message(`New message from ${who}`, {
            action: payload.property_id
              ? {
                  label: 'Open',
                  onClick: () =>
                    navigate(`/summary/${payload.property_id}/messages`),
                }
              : undefined,
          });
          return;
        }
        if (payload.kind === 'analysis_completed') {
          const where = payload.property_address?.trim();
          toast.message(
            where ? `Analysis ready for ${where}` : 'Analysis ready',
            {
              action: {
                label: 'Open',
                onClick: () => navigate(`/summary/${payload.property_id}`),
              },
            },
          );
          return;
        }
        if (payload.kind === 'analysis_rerunning') {
          const who = payload.uploader_name?.trim() || 'Someone';
          const where = payload.property_address?.trim() || 'a property';
          toast.message(`${who} added a new document`, {
            description: `Re-analyzing ${where}`,
            action: {
              label: 'Open',
              onClick: () => navigate(`/summary/${payload.property_id}`),
            },
          });
          return;
        }
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(`[useNotificationsChannel] subscribe ${status} for ${channelName}`);
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, navigate]);
}

function humanizeRole(role: string): string {
  switch (role) {
    case 'co_listing_agent':
      return 'Co-listing agent';
    case 'listing_tc':
      return 'Listing TC';
    case 'seller':
      return 'Seller';
    case 'buying_agent':
      return "Buyer's agent";
    case 'buyer':
      return 'Buyer';
    case 'buying_tc':
      return 'Buying TC';
    default:
      return role;
  }
}
